import { BehaviorSubject } from "rxjs";
import { map, skipWhile } from "rxjs/operators";
import moment from 'moment-timezone';
import CognitoUtils from '../../shared/cognitoUtils';
import { JWT_TOKEN_NAME } from "../../constants";
let companyDateUserShiftsMap = {};

export default class FutureShiftsService {
  /* @ngInject */
  constructor($window, $cookies, Jobs, AuthService) {
    this.$window = $window;
    this.$cookies = $cookies;
    this.jobs = Jobs;
    this.authService = AuthService;
    this.userFutureShiftsByCompany = new BehaviorSubject(companyDateUserShiftsMap);
    this.batchSize = 25;
  }

  // TODO move this to auth service
  _verifyV2Token = async () => {
    // We don't need to check if JWT exists locally if we're using cognito tokens
    return this.authService.loggedIn();
  }

  _createUserBatches = (crewMembers) => {
    const userCount = crewMembers.length;
    let batches = Math.round(userCount / this.batchSize);
    if(userCount % this.batchSize !== 0) {
      batches++;
    }
    const userIdBatches = [];
    for(let i = 0; i < batches; i++) {
      let batchStart = this.batchSize * i; // 1
      let batchEnd = this.batchSize * (i + 1); // 2
      if(batchEnd > userCount) { // false
        batchEnd = userCount;
      }
      userIdBatches[i] = [];
      let currentBatch = userIdBatches[i];
      while(batchStart < batchEnd) {
        currentBatch.push(crewMembers[batchStart].id);
        batchStart++;
      }
    }
    return userIdBatches;
  }

  /**
   * Gets future shifts for a list of crewmemberIds and stores it hashed by companyId and targetDate
   * @param companyId number
   * @param targetDate Date
   * @param crewMembers number[]
   * @param dateHash Date
   * @returns {Observable<Array<{futureShiftCount, jobs[], lastFutureShift}>}
   *
   * TODO: Delete this method because it's replaced by getFutureShiftsObservable and fetchFutureShiftsForBatch
   */
   getFutureShiftsFromDateByCompanyByUserIds = (companyId, targetDate, crewMembers, dateHash) => {
    const hash = this._getMapHash(companyId, moment(dateHash).toString());
    if(!companyDateUserShiftsMap[hash]) {
      // no results for that hash yet, call the back end
      // we need to verify that the token for api-v2 is present because auth.getTokens has an asynchronous call to get the token
      // but the method isn't async so this code was commonly getting called with an undefined jwt due to a race condition.
      // _verifyV2Token will wrap that call in a promise with retries and a set timeout to wait for a token to show up.
      companyDateUserShiftsMap[hash] = {};
      this._verifyV2Token().then(async () => {
          const userIdBatches = this._createUserBatches(crewMembers);
          let first = true;
          let timeout = 0;
          // throttle the calls
          userIdBatches.forEach(userIds => {
              if(!first) {
                  timeout = 200;
              }
              setTimeout(() => {
                  this.jobs.getFutureShifts(companyId, userIds, targetDate).then((results) => {
                      companyDateUserShiftsMap[hash] = {
                          ...results.futureShifts,
                          ...companyDateUserShiftsMap[hash]
                      };
                      this.userFutureShiftsByCompany.next(companyDateUserShiftsMap);
                  })
              }, timeout);
          })
      })
    }

    return this.userFutureShiftsByCompany.asObservable().pipe(
      map(map => map[hash] ),
      skipWhile(map => map === undefined),
    );
  }

    /**
     * Returns an observable for a specific company/date combination
     * This allows multiple batches to update the same observable
     */
    getFutureShiftsObservable(companyId, dateHash) {
        const hash = this._getMapHash(companyId, moment(dateHash).toString());

        if (!companyDateUserShiftsMap[hash]) {
            companyDateUserShiftsMap[hash] = {};
        }

        return this.userFutureShiftsByCompany.asObservable().pipe(
            map(map => map[hash]),
            skipWhile(map => map === undefined || Object.keys(map).length === 0),
        );
    }

    /**
     * Fetch future shifts for a specific batch without creating a new subscription
     */
    fetchFutureShiftsForBatch(companyId, targetDate, crewMembers, dateHash) {
        const hash = this._getMapHash(companyId, moment(dateHash).toString());

        if (!companyDateUserShiftsMap[hash]) {
            companyDateUserShiftsMap[hash] = {};
        }

        this._verifyV2Token().then(async () => {
            const userIdBatches = this._createUserBatches(crewMembers);

            // Process each batch with a small delay to avoid overwhelming the API
            userIdBatches.forEach((userIds, index) => {
                const timeout = index * 200; // Stagger API calls

                setTimeout(() => {
                    this.jobs.getFutureShifts(companyId, userIds, targetDate).then((results) => {
                        // Merge results into the existing hash
                        companyDateUserShiftsMap[hash] = {
                            ...companyDateUserShiftsMap[hash],
                            ...results.futureShifts
                        };
                        // Notify all subscribers
                        this.userFutureShiftsByCompany.next(companyDateUserShiftsMap);
                    });
                }, timeout);
            });
        });
    }

  resetFutureShiftsMap() {
    companyDateUserShiftsMap = {};
  }

  /**
   *
   * @param companyId number
   * @param dateHash string
   * @returns {string}
   * @private
   */
  _getMapHash = (companyId, dateHash) => {
    if(!dateHash) {
      throw new error("no date hash given to future-shift-service.getFutureShiftsFromDateByCompanyByUserId");
    }
    return companyId.toString() + "-" + moment(dateHash).format("MMDDYYYY").toString();
  }
}

FutureShiftsService.$inject = ['$window', '$cookies', 'Jobs', 'AuthService'];
